// P9/S8 — İK Asistanı wire layer (spec 70 feat_support_ai_assistant).
//
// Two calls, both OUTSIDE the openapi-fetch typed client on purpose:
//   * askAssistant — POST /v1/ai/stream with capability_id=ai_help_assistant,
//     through the shared capability stream (src/api/ai-capability-stream.ts),
//     which the reports screen's AI tab also uses for ai_hr_reporting.
//   * escalateAssistant — POST /v1/support/assistant/escalate. The S8 route
//     declares no OpenAPI schema and openapi.yaml regen is serialized at the
//     phase gate (S9), so this is a raw enveloped fetch; the shape mirrors
//     the route serializer. Carries x-idempotency-key — the CALLER owns the
//     key so a retry of the same confirmation replays instead of duplicating.

import { aiAuthHeaders, postAiCapabilityStream, readSseEvents } from "@/api/ai-capability-stream";
import { platformGraphBaseUrl } from "@/api/platform-graph";

export const ASSISTANT_CAPABILITY_ID = "ai_help_assistant";

export type AssistantCitation = {
  readonly article_id: string; readonly slug: string; readonly title: string; readonly locale: string;
} | {
  readonly source_kind: "policy"; readonly policy_id: string; readonly version_id: string;
  readonly title: string; readonly locale: string;
};
export function assistantCitationHref(citation: AssistantCitation): string {
  return "policy_id" in citation
    ? `/me/policies/${encodeURIComponent(citation.policy_id)}?version=${encodeURIComponent(citation.version_id)}`
    : `/help?article=${encodeURIComponent(citation.article_id)}`;
}

/** SSE events the assistant capability emits (routes/support-assistant.ts). */
export type AssistantStreamEvent =
  | { readonly type: "delta"; readonly text: string }
  | { readonly type: "citations"; readonly citations: readonly AssistantCitation[] }
  | {
      readonly type: "refusal";
      readonly message_key: string;
      readonly escalation_offered: boolean;
      /** SEM-6346 — who to go to instead, when the refused question asked for
       *  an operation the caller has no authority over. Absent otherwise. */
      readonly referral_key?: string;
    }
  | {
      readonly type: "done";
      readonly disposition: "answered" | "refused";
      /** Server-issued conversation id; echo on a follow-up or escalation. */
      readonly thread_id?: string;
    }
  | {
      readonly type: "error";
      readonly message?: string;
      /** The stream may still have created a conversation before failing. */
      readonly thread_id?: string;
    };

/** Thrown when the surface is flag-disabled (spec-58 503 unavailable). */
export class AssistantDisabledError extends Error {
  constructor() {
    super("support assistant is disabled");
    this.name = "AssistantDisabledError";
  }
}

/**
 * Ask the assistant; yields typed SSE events as they arrive. The caller
 * passes an AbortSignal so a newer question (or unmount) cancels the
 * in-flight stream (abort-race-guard discipline).
 */
export async function* askAssistant(
  prompt: string,
  options: { readonly signal?: AbortSignal; readonly threadId?: string } = {},
): AsyncGenerator<AssistantStreamEvent, void> {
  const res = await postAiCapabilityStream(ASSISTANT_CAPABILITY_ID, prompt, {
    ...(options.signal !== undefined && { signal: options.signal }),
    ...(options.threadId !== undefined && { body: { thread_id: options.threadId } }),
  });
  if (res.status === 503) throw new AssistantDisabledError();
  if (!res.ok || !res.body) throw new Error(`assistant stream failed: HTTP ${res.status}`);
  yield* readSseEvents<AssistantStreamEvent>(res);
}

export interface EscalationDraft {
  readonly subject: string;
  readonly summary_markdown: string;
  readonly category?: "hr" | "payroll" | "benefits" | "it" | "access" | "other";
  /** Server-issued assistant conversation id for SEM-2015 deduplication. */
  readonly thread_id?: string;
}

export interface EscalatedTicket {
  readonly ticket_id: string;
  readonly status: string;
  readonly source: string;
  readonly subject: string;
}

/**
 * Open the user-confirmed escalation ticket. `idempotencyKey` is minted once
 * per confirmation dialog by the caller — a retry of the SAME confirmation
 * replays the original ticket instead of opening a second one.
 */
export async function escalateAssistant(
  draft: EscalationDraft,
  idempotencyKey: string,
): Promise<EscalatedTicket> {
  const res = await fetch(`${platformGraphBaseUrl}/v1/support/assistant/escalate`, {
    method: "POST",
    headers: { ...aiAuthHeaders(), "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(draft),
  });
  if (res.status === 503) throw new AssistantDisabledError();
  const body = (await res.json()) as {
    data?: { ticket?: EscalatedTicket };
    errors?: ReadonlyArray<{ user_message?: string; message?: string }>;
  };
  if (!res.ok || body.data?.ticket === undefined) {
    const first = body.errors?.[0];
    throw new Error(first?.user_message ?? first?.message ?? `escalation failed: HTTP ${res.status}`);
  }
  return body.data.ticket;
}
